import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import SideMenu from "./components/SideMenu";
import TrialExpiredModal from "./components/TrialExpiredModal";
import TrialStatusModal from "./components/TrialStatusModal";
import TrialExpiredContent from "./components/TrialExpiredContent";
import TrialBar from "./components/TrialBar";
import {
  getUserData,
  setUserData,
  getTimeUntilExpiry,
  logout,
} from "./utils/auth";
import { authenticatedFetch, createStripeCheckoutSession } from "./config/api";
import { SUPPORTED_LOCALES } from "./contexts/LocaleContext";

function InvoitioLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="3"
        className="fill-emerald-400"
      />
      <path d="M17 4v4h4" className="fill-emerald-500" />
      <circle cx="10" cy="11" r="1" className="fill-slate-800" />
      <circle cx="14" cy="11" r="1" className="fill-slate-800" />
      <path
        d="M9.5 14c1.2 1.2 3.8 1.2 5 0"
        stroke="#0f172a"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function Layout() {
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  // Trial logic
  const [showTrialExpired, setShowTrialExpired] = useState(false);
  const [showTrialStatus, setShowTrialStatus] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    const timeUntilExpiry = getTimeUntilExpiry(token);
    if (timeUntilExpiry !== null) {
      console.log(
        `Token expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes`
      );

      const timer = setTimeout(async () => {
        console.warn("Token expired, triggering auto-logout...");
        await logout();
        const path = window.location.pathname;
        const localeMatch = path.match(/^\/([a-z]{2})(\/|$)/);
        const locale = localeMatch ? localeMatch[1] : "en";
        window.location.href = `/${locale}`;
      }, timeUntilExpiry);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismissTrialStatus = () => {
    setShowTrialStatus(false);
    sessionStorage.setItem("trial-modal-dismissed", "true");
  };

  useEffect(() => {
    const checkTrial = async () => {
      // This Layout is only mounted for authenticated routes.
      // But we still need correct route matching for locale-prefixed routes like /en/app.
      const normalizePath = (pathname: string) => {
        const parts = pathname.split("/").filter(Boolean);
        const maybeLocale = parts[0];
        if (maybeLocale && SUPPORTED_LOCALES.includes(maybeLocale as any)) {
          const rest = parts.slice(1).join("/");
          return "/" + rest;
        }
        return pathname;
      };

      const normalizedPath = normalizePath(location.pathname);

      const protectedPages = [
        "/app",
        "/invoice-list",
        "/statistics",
        "/transfer-invoices",
        "/repeating-invoices",
        "/client-notifications",
        "/expenses",
        "/settings",
      ];
      const isProtectedPage = protectedPages.some((p) =>
        normalizedPath.startsWith(p)
      );

      if (!isProtectedPage) {
        setShowTrialExpired(false);
        setShowTrialStatus(false);
        return;
      }

      const token = localStorage.getItem("accessToken");
      if (!token) return;

      let userData = getUserData();

      // Always refresh profile periodically on protected pages (important after Stripe payment redirect)
      // so we don't keep showing trial modals with stale cached userData.
      const lastFetch = Number(
        sessionStorage.getItem("profile-last-fetch") || "0"
      );
      const isTrialUser = !userData?.hasActiveSubscription;
      // Always refresh for trial users as requested, 30s for paid
      const shouldRefresh =
        isTrialUser || !lastFetch || Date.now() - lastFetch > 30_000;

      if (
        !userData ||
        !userData.createdAt ||
        userData.hasActiveSubscription === undefined ||
        shouldRefresh
      ) {
        try {
          // Log visit only once per browser session
          const sessionLogged = sessionStorage.getItem("visit-logged");
          const url = sessionLogged ? "/api/user/profile" : "/api/user/profile?logVisit=true";
          
          const response = await authenticatedFetch(url);
          if (response.ok) {
            const freshData = await response.json();
            if (freshData) {
              setUserData(freshData);
              userData = freshData;
              sessionStorage.setItem("profile-last-fetch", String(Date.now()));
              if (!sessionLogged) {
                sessionStorage.setItem("visit-logged", "true");
              }
            }
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }

      if (userData) {
        setShowTrialExpired(!!userData.is_trial_expired);
        const remaining = userData.trial_days_remaining ?? 0;
        setDaysRemaining(remaining);

        if (!userData.hasActiveSubscription && !userData.is_trial_expired) {
          // Show "remaining days" modal if not dismissed in this session
          const dismissed = sessionStorage.getItem("trial-modal-dismissed");
          if (!dismissed) {
            setShowTrialStatus(true);
          } else {
            setShowTrialStatus(false);
          }
        } else {
          setShowTrialStatus(false);
        }
      }
    };

    checkTrial();
  }, [location.pathname]);

  // Detect desktop via matchMedia (tailwind lg breakpoint ~1024px)
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Update document title based on route
  useEffect(() => {
    const routeTitles: Record<string, string> = {
      "/app": "Invoice Generator - Invoitio",
      "/invoice-list": "Invoice List - Invoitio",
      "/statistics": "Statistics - Invoitio",
      "/transfer-invoices": "Transfer Invoices - Invoitio",
      "/repeating-invoices": "Repeating Invoices - Invoitio",
      "/client-notifications": "Client Notifications - Invoitio",
      "/expenses": "Expenses - Invoitio",
      "/settings": "User Settings - Invoitio",
    };
    const title =
      routeTitles[location.pathname] || "Invoitio - Invoice Generator";
    document.title = title;
  }, [location.pathname]);

  const handleSubscribe = async (plan: "monthly" | "yearly" | "lifetime") => {
    try {
      const { url } = await createStripeCheckoutSession(plan);
      window.location.href = url;
    } catch (error) {
      console.error("Error creating stripe checkout session:", error);
      alert("Failed to initiate checkout. Please try again later.");
    }
  };

  // Normalize locale-prefixed routes like /en/app -> /app
  const normalizedPath = (() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const maybeLocale = parts[0];
    if (maybeLocale && SUPPORTED_LOCALES.includes(maybeLocale as any)) {
      return "/" + parts.slice(1).join("/");
    }
    return location.pathname;
  })();

  const allowBlocker = normalizedPath !== "/settings";

  const userData = getUserData();
  const isTrial =
    !userData?.hasActiveSubscription &&
    !showTrialExpired &&
    daysRemaining !== null &&
    daysRemaining > 0;

  return (
    <div
      className={`${
        isDesktop ? "h-screen overflow-hidden" : "min-h-screen"
      } bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 transition-colors duration-200`}
    >
      <div className={`flex ${isDesktop ? "h-full" : ""}`}>
        <SideMenu
          isDesktop={isDesktop}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          userRole={userData?.role}
        />

        <main
          className={`flex-1 min-w-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors duration-200 ${
            isDesktop ? "h-full overflow-y-auto" : ""
          }`}
        >
          {/* Top bar only on small screens to open drawer */}
          {!isDesktop && (
            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-colors duration-200 sticky top-0 z-[120]">
              <div className="px-4 py-3 flex items-center justify-between">
                <button
                  onClick={() => setMobileOpen(true)}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 border-0 outline-none transition-colors"
                  aria-label="Open menu"
                >
                  <svg
                    className="w-6 h-6 dark:text-slate-100"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M4 6h16M4 12h16M4 18h16"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  <InvoitioLogo className="h-5 w-5" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Invoitio
                  </span>
                </div>
                <div className="w-10" />
              </div>
            </div>
          )}

          {isTrial && (
            <TrialBar
              daysRemaining={daysRemaining ?? 0}
              onSubscribe={handleSubscribe}
            />
          )}

          <div className="app p-4 lg:p-6">
            {!isDesktop && showTrialExpired && allowBlocker ? (
              <TrialExpiredContent onSubscribe={handleSubscribe} isPage />
            ) : (
              <Outlet
                context={{
                  isBlockerActive:
                    (showTrialExpired || showTrialStatus) && allowBlocker,
                }}
              />
            )}
          </div>
        </main>
      </div>

      <TrialExpiredModal
        isOpen={isDesktop && showTrialExpired && allowBlocker}
        onSubscribe={handleSubscribe}
      />

      <TrialStatusModal
        isOpen={isDesktop && showTrialStatus && allowBlocker}
        onClose={handleDismissTrialStatus}
        daysRemaining={daysRemaining ?? 0}
        onSubscribe={handleSubscribe}
      />
    </div>
  );
}
